/**
 * Runtime analytics loader — fetches /api/config and conditionally injects
 * server-configured Meta Pixel + Google Search Console verification tags.
 *
 * NOTE: Google Analytics 4 (G-LNP21FLDGZ) is loaded directly in index.html
 * with Consent Mode v2 (defaults to denied). ConsentBanner.jsx upgrades
 * the GA4 consent state after user opt-in. This file no longer injects GA4
 * to prevent duplicate script loading.
 *
 * Meta Pixel is CONSENT-GATED via the same `sz_consent_analytics` key
 * used by Clarity + PostHog. If the user has not yet granted consent,
 * ``initAnalytics`` stashes the configured pixel id on
 * ``window.__szPendingMetaPixelId`` so ``ConsentBanner.accept()`` can
 * invoke ``window.__szInitMetaPixel(id)`` after opt-in.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const CONSENT_KEY = "sz_consent_analytics";

function _consentGranted() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

function _actuallyInjectMetaPixel(pixelId) {
  if (!pixelId) return;
  if (typeof window !== "undefined" && window.__szMetaPixelInitialized) return;
  if (document.getElementById("sz-metapixel")) {
    window.__szMetaPixelInitialized = true;
    return;
  }
  const s = document.createElement("script");
  s.id = "sz-metapixel";
  s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
  document.head.appendChild(s);
  window.__szMetaPixelInitialized = true;
}

// Global entry point that ConsentBanner.accept() invokes after opt-in.
// Idempotent — safe to call more than once; only the first call injects.
if (typeof window !== "undefined") {
  window.__szInitMetaPixel = function (explicitPixelId) {
    if (window.__szMetaPixelInitialized) return;
    if (!_consentGranted()) return;
    const pixelId = explicitPixelId || window.__szPendingMetaPixelId;
    _actuallyInjectMetaPixel(pixelId);
  };
}

function injectMetaPixel(pixelId) {
  if (!pixelId) return;
  if (typeof window === "undefined") return;
  // Remember what backend told us so ConsentBanner can init after accept
  // even though this runs at mount, well before any consent choice.
  window.__szPendingMetaPixelId = pixelId;
  if (_consentGranted()) {
    _actuallyInjectMetaPixel(pixelId);
  }
  // else: no injection until window.__szInitMetaPixel is called by
  // the consent banner.
}

function injectSiteVerification(token) {
  if (!token || document.querySelector('meta[name="google-site-verification"]')) return;
  const m = document.createElement("meta");
  m.name = "google-site-verification";
  m.content = token;
  document.head.appendChild(m);
}

export async function initAnalytics() {
  if (!BACKEND_URL) return;
  try {
    const res = await fetch(`${BACKEND_URL}/api/config`, { credentials: "omit" });
    if (!res.ok) return;
    const cfg = await res.json();
    const a = cfg?.analytics || {};
    // GA4 is loaded in index.html — do not inject here.
    // Meta Pixel is consent-gated via injectMetaPixel().
    injectMetaPixel(a.meta_pixel_id);
    // Site verification is a static <meta> tag, not a tracker — safe to
    // inject regardless of consent.
    injectSiteVerification(a.google_site_verification);
  } catch {
    // silent
  }
}
